// Import react.
import * as React from 'react';
// Import canvas
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

interface UseDomToPdfPrintParameters {
	elementTagsToIgnore?: string[];
	elementClassesToIgnore?: string[];
}

interface UseDomToPdfPrintResult {
	print: () => Promise<void>;
	contentRef: React.RefObject<HTMLDivElement>;
}

const useDomToPdfPrint = (params: UseDomToPdfPrintParameters): UseDomToPdfPrintResult => {

	// #region exported methods.

	const print = async (): Promise<void> => {
		if (contentRef.current !== null) {
			handleElementsToIgnoreByTag();
			handleElementsToIgnoreByClassName();

			const canvas = await html2canvas(contentRef.current);
			const data = canvas.toDataURL('image/png');

			let pdf: jsPDF = new jsPDF({
				orientation: 'portrait',
				unit: 'px',
				format: 'a4',
			});

			const imgProperties = pdf.getImageProperties(data);

			const pdfWidth = pdf.internal.pageSize.getWidth();
			const pdfHeight = (imgProperties.height * pdfWidth) / imgProperties.width;
			let yPosition: number = 0;

			pdf.addImage(data, 'PNG', 0, yPosition, pdfWidth, pdfHeight);

			let heightLeft = pdfHeight;
			heightLeft -= pdf.internal.pageSize.getHeight();

			while (heightLeft >= 0) {
				yPosition = heightLeft - pdfHeight;
				pdf.addPage();
				pdf.addImage(data, 'PNG', 0, yPosition, pdfWidth, pdfHeight);
				heightLeft -= pdf.internal.pageSize.getHeight();
			}

			window.open(pdf.output('bloburl'));		
		}
	}

	// #endregion exported methods.

	// #region refs

	const contentRef = React.useRef<HTMLDivElement>(null);
	
	// #endregion refs

	// #region variables
	
	// #endregion variables

	// #region member methods.

	const handleElementsToIgnoreByTag = (): void => {
		if (params.elementTagsToIgnore !== undefined) {
			params.elementTagsToIgnore.forEach((elementTagToIgnore) => {
				ignoreElementsByTag(elementTagToIgnore);
			});
		}
	}

	const handleElementsToIgnoreByClassName = (): void => {
		if (params.elementClassesToIgnore !== undefined) {
			params.elementClassesToIgnore.forEach((elementClassToIgnore) => {
				ignoreElementsByClassName(elementClassToIgnore);
			});
		}
	}

	const ignoreElementsByTag = (tagName: string): void => {
		if (contentRef.current !== null) {
			// Mark any elements that are to be excluded from the document.
			let elements = contentRef.current.getElementsByTagName(tagName);
			for (let i: number = 0; i < elements.length; i++) {
				elements.item(i)?.setAttribute('data-html2canvas-ignore', 'true');
			}
		}
	}

	const ignoreElementsByClassName = (className: string): void => {
		if (contentRef.current !== null) {
			// Mark any elements that are to be excluded from the document.
			let elements = contentRef.current.getElementsByClassName(className);
			for (let i: number = 0; i < elements.length; i++) {
				elements.item(i)?.setAttribute('data-html2canvas-ignore', 'true');
			}
		}
	}

	// #endregion member methods.

	return {
		print,
		contentRef,
	};
}

// Export parameters.
export {
	UseDomToPdfPrintParameters,
};

// Export results.
export {
	UseDomToPdfPrintResult,
};

export default useDomToPdfPrint;